import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import { fmt3, round3, num } from './allocationHelpers';

const fmtDate = (v) => (v && moment(v).isValid() && moment(v).year() > 1900 ? moment(v).format('DD-MMM-YYYY') : '');

// Keeps what the user is typing locally and commits on blur / Enter so a rejected value can revert.
const AmountCell = ({ value, disabled, onCommit }) => {
    const [draft, setDraft] = useState(fmt3(value));
    useEffect(() => { setDraft(fmt3(value)); }, [value]);
    const commit = () => {
        onCommit(draft);
        setDraft(fmt3(value));
    };
    return (
        <input
            type="number"
            step="0.001"
            className="p-1 text-end"
            style={{ width: 110 }}
            disabled={disabled}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
    );
};

const AllocationGrid = ({ rows, readOnly, loading, onToggle, onAmountCommit }) => {
    const th = { whiteSpace: 'nowrap' };
    const right = { textAlign: 'right' };
    const totals = rows.reduce((t, r) => ({ amount: t.amount + num(r.amount), balance: t.balance + num(r.balance) }), { amount: 0, balance: 0 });

    return (
        <div className="table-container" style={{ overflowX: 'auto', maxHeight: 380, overflowY: 'auto' }}>
            <table className="table table-bordered mb-0">
                <thead className="dynoTable">
                    <tr>
                        <th style={th}>Select</th>
                        <th style={th}>Voucher No</th>
                        <th style={th}>Voucher Date</th>
                        <th style={th}>Invoice No</th>
                        <th style={{ ...th, ...right }}>Invoice Amount</th>
                        <th style={{ ...th, ...right }}>VAT</th>
                        <th style={{ ...th, ...right }}>ST</th>
                        <th style={{ ...th, ...right }}>Total</th>
                        <th style={{ ...th, ...right }}>Amount Paid</th>
                        <th style={{ ...th, ...right }}>Amount</th>
                        <th style={{ ...th, ...right }}>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={11} className="text-center p-3"><ClipLoader size={22} color="#6c5ce7" /></td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                        <tr><td colSpan={11} className="text-center text-muted p-3">No outstanding invoices.</td></tr>
                    )}
                    {!loading && rows.map((row, index) => (
                        <tr key={`${row.receiptNo}-${index}`}>
                            <td className="text-center">
                                <input
                                    type="checkbox"
                                    checked={!!row.selected}
                                    disabled={readOnly}
                                    onChange={() => onToggle && onToggle(index)}
                                />
                            </td>
                            <td>{row.voucherNo}</td>
                            <td>{fmtDate(row.voucherDate)}</td>
                            <td>{row.invoiceNo}</td>
                            <td style={right}>{fmt3(row.invoiceAmount)}</td>
                            <td style={right}>{fmt3(row.vat)}</td>
                            <td style={right}>{fmt3(row.st)}</td>
                            <td style={right}>{fmt3(row.totalAmount)}</td>
                            <td style={right}>{fmt3(row.amountPaid)}</td>
                            <td style={right}>
                                {readOnly
                                    ? fmt3(row.amount)
                                    : <AmountCell value={row.amount} disabled={!row.selected} onCommit={(v) => onAmountCommit(index, v)} />}
                            </td>
                            {/* Read-only (edit/view) lines already carry the balance; in create it is live: balance - amount */}
                            <td style={right}>{fmt3(readOnly ? row.balance : round3(row.balance) - round3(row.amount))}</td>
                        </tr>
                    ))}
                </tbody>
                {rows.length > 0 && (
                    <tfoot>
                        <tr>
                            <td colSpan={9}><strong>Total</strong></td>
                            <td style={right}><strong>{fmt3(totals.amount)}</strong></td>
                            <td />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default AllocationGrid;
